import React, { useState, useRef, useEffect } from 'react';
import { Camera, Upload, X, Check, Loader2, RefreshCw } from 'lucide-react';
import { autoCoringService, GradedPart, GradeResult } from '../../services/auto-coring/AutoCoringService';
import KaTeXRenderer from '../common/KaTeXRenderer';
import ResultEditor from '../../components/auto-coring/ResultEditor';
import ImageCropper from '../common/ImageCropper';
import toast from 'react-hot-toast';

interface EssayQuestionGraderProps {
    questionId: string;
    questionContent: string;
    correctAnswer: string;
    onGradeComplete: (result: GradeResult) => void;
    isDarkMode?: boolean;
    onUploadStatusChange?: (isUploading: boolean) => void;
    initialResult?: GradeResult; // Add initialResult prop
}

export const EssayQuestionGrader: React.FC<EssayQuestionGraderProps> = ({
    questionId,
    questionContent,
    correctAnswer,
    onGradeComplete,
    isDarkMode = false,
    onUploadStatusChange,
    initialResult
}) => {
    // Initialize state based on initialResult
    const [mode, setMode] = useState<'initial' | 'camera' | 'crop' | 'processing' | 'verify' | 'result' | 'error'>(
        initialResult ? 'result' : 'initial'
    );
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(initialResult?.imageUrl || null);
    const [scanResult, setScanResult] = useState<{ parts: GradedPart[], rawText?: string } | null>(null);
    const [gradeResult, setGradeResult] = useState<GradeResult | null>(initialResult || null);
    const [processingStep, setProcessingStep] = useState<string>('');

    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isWaitingForFile = useRef(false);

    // ✅ Sync state with initialResult when it changes (e.g. after parent update)
    useEffect(() => {
        if (initialResult) {
            console.log('🔄 [EssayQuestionGrader] Syncing with initialResult:', initialResult);
            setGradeResult(initialResult);
            if (initialResult.imageUrl) {
                setImagePreview(initialResult.imageUrl);
            }
            // Only switch to result mode if we are in initial mode (don't interrupt other modes)
            setMode(prev => prev === 'initial' ? 'result' : prev);
        }
    }, [initialResult]);

    // Handle file picker cancellation via window focus
    useEffect(() => {
        const handleWindowFocus = () => {
            if (isWaitingForFile.current) {
                // Window regained focus. Wait to see if change event fires.
                setTimeout(() => {
                    // If still waiting and mode is still initial, assume cancel
                    if (isWaitingForFile.current && mode === 'initial') {
                        console.log('❌ [EssayQuestionGrader] Window focused but no file selected (Cancel detected)');
                        if (onUploadStatusChange) onUploadStatusChange(false);
                        isWaitingForFile.current = false;
                    }
                }, 1000); // 1s delay to be safe
            }
        };

        window.addEventListener('focus', handleWindowFocus);
        return () => window.removeEventListener('focus', handleWindowFocus);
    }, [mode, onUploadStatusChange]);

    // Cleanup
    useEffect(() => {
        return () => {
            stopCamera();
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, []);

    const startCamera = async () => {
        if (onUploadStatusChange) onUploadStatusChange(true);

        // Fix Race Condition: Wait for state to propagate before accessing camera
        setTimeout(async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' }
                });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
                streamRef.current = stream;
                setMode('camera');
            } catch (err) {
                console.error("Error accessing camera:", err);
                toast.error("Không thể truy cập camera. Vui lòng kiểm tra quyền truy cập.");
                if (onUploadStatusChange) onUploadStatusChange(false);
            }
        }, 500);
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
    };

    const captureImage = () => {
        if (videoRef.current) {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(videoRef.current, 0, 0);
                canvas.toBlob((blob) => {
                    if (blob) {
                        stopCamera();
                        const url = URL.createObjectURL(blob);
                        setImagePreview(url);
                        setMode('crop');
                    }
                }, 'image/jpeg');
            }
        }
    };

    const handleFileSelect = (file: File) => {
        console.log('📤 [EssayQuestionGrader] File selected, entering crop mode');

        // ✅ CRITICAL: Set upload status IMMEDIATELY when entering crop mode
        // This prevents fullscreen exit when ImageCropper renders
        if (onUploadStatusChange) {
            onUploadStatusChange(true);
            console.log('✅ [EssayQuestionGrader] Upload status set to TRUE (crop mode)');
        }

        const url = URL.createObjectURL(file);
        setImagePreview(url);
        setMode('crop');
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        isWaitingForFile.current = false; // No longer waiting

        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        } else {
            // User canceled file picker - reset upload status
            console.log('❌ [EssayQuestionGrader] File picker canceled, resetting upload status');
            if (onUploadStatusChange) onUploadStatusChange(false);
        }
        // Reset file input value to allow re-selecting same file
        if (e.target) {
            e.target.value = '';
        }
    };

    const handleCropComplete = async (croppedBlob: Blob) => {
        // Upload status already set in handleFileSelect, keep it true during processing
        console.log('✂️ [EssayQuestionGrader] Crop completed, processing...');

        const file = new File([croppedBlob], "submission.jpg", { type: "image/jpeg" });
        setImageFile(file);

        // Update preview
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }
        const url = URL.createObjectURL(croppedBlob);
        setImagePreview(url);

        setMode('processing');
        await processScan(file);
    };

    const handleCancelCrop = () => {
        // Clean up preview URL
        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }
        setImagePreview(null);
        setMode('initial');
        // Reset upload status when canceling
        if (onUploadStatusChange) onUploadStatusChange(false);
    };

    const processScan = async (file: File) => {
        try {
            setProcessingStep('Đang đọc nội dung ảnh...');
            const scanRes = await autoCoringService.scanSubmission(file, questionContent);
            setScanResult(scanRes);
            setMode('verify');

            // ✅ KEEP upload status TRUE during verification
            // We do NOT reset it here anymore. It will be reset when user clicks "Xong" or "Hủy".
            console.log('✅ [EssayQuestionGrader] Scan completed, entering verify mode (Upload status kept TRUE)');
        } catch (error) {
            console.error("Processing error:", error);
            const msg = error instanceof Error ? error.message : "Có lỗi xảy ra. Vui lòng thử lại.";
            setErrorMessage(msg);
            toast.error(msg);
            setMode('error');

            // Reset upload status on error
            console.log('❌ [EssayQuestionGrader] Scan failed, resetting upload status');
            if (onUploadStatusChange) onUploadStatusChange(false);
        }
    };

    const handleConfirmVerify = async (updatedParts: GradedPart[], options: { penalizeMissingUnit: boolean }) => {
        // Just save the result (Pending state)
        const pendingResult: GradeResult = {
            parts: updatedParts,
            totalScore: -1, // Mark as pending
            totalMaxScore: 0,
            overallFeedback: "Bài làm đã được lưu. Hệ thống sẽ chấm điểm khi bạn nộp bài.",
            teacherSolution: "",
            imageUrl: imagePreview || undefined
        };

        // Notify parent to save answer
        onGradeComplete(pendingResult);

        // Switch to result mode to show "Saved" state
        setMode('result');
        toast.success("Đã lưu kết quả!");

        // ✅ Reset upload status NOW, after user has confirmed
        console.log('✅ [EssayQuestionGrader] Verification confirmed, resetting upload status');
        if (onUploadStatusChange) onUploadStatusChange(false);
    };

    const handleReset = () => {
        setMode('initial');
        setImageFile(null);
        setImagePreview(null);
        setScanResult(null);
        setGradeResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        // ✅ Reset upload status if user cancels/resets
        console.log('🔄 [EssayQuestionGrader] Resetting state and upload status');
        if (onUploadStatusChange) onUploadStatusChange(false);
    };

    const renderInitial = () => (
        <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
            onClick={() => {
                // ✅ Prevent fullscreen exit: Set upload status BEFORE opening file dialog
                if (onUploadStatusChange) onUploadStatusChange(true);
                isWaitingForFile.current = true; // Mark as waiting

                // Small delay to ensure state propagates
                setTimeout(() => {
                    fileInputRef.current?.click();
                }, 100);
            }}
        >
            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
                <Upload className="w-8 h-8" />
            </div>
            <p className="text-gray-900 font-medium mb-2">Tải ảnh bài làm lên</p>
            <p className="text-base text-gray-500 mb-4">Hỗ trợ JPG, PNG</p>
            <div className="flex gap-3">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        startCamera();
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Camera className="w-4 h-4" />
                    Chụp ảnh
                </button>
                <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    Chọn ảnh
                </button>
            </div>
        </div>
    );

    const renderCamera = () => (
        <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
            <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
            />
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                <button
                    onClick={stopCamera}
                    className="p-3 bg-white/20 backdrop-blur-sm text-white rounded-full hover:bg-white/30 transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>
                <button
                    onClick={captureImage}
                    className="p-4 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors shadow-lg"
                >
                    <Camera className="w-8 h-8" />
                </button>
            </div>
        </div>
    );

    const renderProcessing = () => (
        <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-blue-200 rounded-xl bg-blue-50">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
            <p className="text-xl font-medium text-blue-900">{processingStep}</p>
            <p className="text-base text-blue-600 mt-2">Vui lòng đợi trong giây lát...</p>
        </div>
    );

    const renderVerify = () => (
        <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-gray-800">Xác nhận nội dung bài làm</h4>
                <button
                    onClick={handleReset}
                    className="text-base text-gray-500 hover:text-red-500"
                >
                    Hủy bỏ
                </button>
            </div>
            {scanResult && (
                <ResultEditor
                    parts={scanResult.parts}
                    onConfirm={handleConfirmVerify}
                />
            )}
        </div>
    );

    const renderResult = () => {
        if (!gradeResult) return null;

        // Check if pending
        const isPending = gradeResult.totalScore === -1;

        return (
            <div className="animate-fade-in">
                <div className={`p-4 rounded-xl border mb-4 ${isPending ? 'bg-blue-50 border-blue-200' : (gradeResult.totalScore === gradeResult.totalMaxScore ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200')}`}>
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-700">
                            {isPending ? 'Trạng thái:' : 'Kết quả chấm điểm:'}
                        </span>
                        <span className={`text-2xl font-bold ${isPending ? 'text-blue-700' : (gradeResult.totalScore === gradeResult.totalMaxScore ? 'text-green-700' : 'text-orange-700')}`}>
                            {isPending ? 'Đã lưu (Chờ chấm)' : `${gradeResult.totalScore}/${gradeResult.totalMaxScore}`}
                        </span>
                    </div>
                    <p className="text-gray-600">{gradeResult.overallFeedback}</p>
                </div>

                <div className="space-y-3 mb-4">
                    {gradeResult.parts.map((part, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border ${isPending ? 'bg-gray-50 border-gray-200' : (part.isCorrect ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100')}`}>
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2">
                                    {!isPending && (
                                        part.isCorrect ? (
                                            <Check className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <X className="w-4 h-4 text-red-600" />
                                        )
                                    )}
                                    <span className={`text-base font-medium ${isPending ? 'text-gray-700' : (part.isCorrect ? 'text-green-700' : 'text-red-700')}`}>
                                        {isPending ? 'Nội dung đã lưu:' : part.feedback}
                                    </span>
                                </div>
                                {!isPending && (
                                    <span className="text-base font-bold text-gray-900">
                                        {part.score}/{part.maxScore}
                                    </span>
                                )}
                            </div>
                            <div className="bg-white/50 p-2 rounded text-base">
                                <KaTeXRenderer content={part.content} />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        onClick={handleReset}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Làm lại
                    </button>
                </div>
            </div>
        );
    };

    const renderError = () => (
        <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-red-300 rounded-xl bg-red-50">
            <div className="text-red-500 mb-2">
                <X className="w-10 h-10" />
            </div>
            <p className="text-red-600 font-medium text-center mb-4">{errorMessage}</p>
            <button
                onClick={() => setMode('initial')}
                className="px-4 py-2 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            >
                Thử lại
            </button>
        </div>
    );

    return (
        <div className={`mt-4 p-4 rounded-xl border-2 ${isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-blue-100'}`}>
            <h3 className={`font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                <div className="p-1.5 bg-blue-100 text-blue-600 rounded-lg">
                    <Camera className="w-5 h-5" />
                </div>
                Nộp bài tự luận & Chấm điểm AI
            </h3>

            {/* Hidden file input for upload */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                accept="image/*"
                className="hidden"
            />

            {mode === 'initial' && renderInitial()}
            {mode === 'camera' && renderCamera()}
            {mode === 'crop' && imagePreview && (
                <ImageCropper
                    imageUrl={imagePreview}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCancelCrop}
                />
            )}
            {mode === 'processing' && renderProcessing()}
            {mode === 'verify' && renderVerify()}
            {mode === 'result' && renderResult()}
            {mode === 'error' && renderError()}
        </div>
    );
};
